import React from 'react';

export interface EmptyStateProps {
  /** Lucide icon, or any component taking className. Omit for a text-only state. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Short noun phrase — "No bills tracked yet". Not a sentence. */
  title?: string;
  /** One line explaining what to do about it. */
  body?: string;
  /** The action that resolves the emptiness — usually the same CTA as the toolbar. */
  action?: React.ReactNode;
  /**
   * Back-compat with the single-`<p>` version this replaces. Prefer `title`/`body`;
   * a bare `message` renders exactly as before — no icon, no heading.
   */
  message?: string;
  className?: string;
}

/**
 * P2-06. This was a single `<p>` with two text classes, which is why every
 * workspace tab hand-built its own icon + heading + subtext + CTA. It now owns
 * that shape so they can stop.
 *
 * Deliberately NOT a card: an empty state sits inside a panel that is already a
 * card, and nesting two cards is what the surface rule forbids.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  body,
  action,
  message,
  className = '',
}) => {
  const text = body ?? message;

  // The original call shape: no icon, no heading, just a muted line.
  if (!Icon && !title && !action) {
    return (
      <p className={`text-text-secondary dark:text-text-dark-secondary ${className}`}>{text}</p>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center px-6 py-12 text-center ${className}`}>
      {Icon && (
        <span aria-hidden="true" className="mb-4 flex">
          <Icon className="h-10 w-10 text-text-tertiary dark:text-text-dark-tertiary" />
        </span>
      )}
      {title && (
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          {title}
        </h3>
      )}
      {text && (
        <p className="mt-1 max-w-sm text-sm text-text-secondary dark:text-text-dark-secondary">
          {text}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
};

export default EmptyState;
